import React, { useState, useEffect } from "react";
import CancelIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { GridRowModes, DataGrid, GridActionsCellItem, GridRowEditStopReasons, Toolbar, ToolbarButton } from "@mui/x-data-grid";
import { Box, CircularProgress, Grid } from "@mui/material";
import { getWithdrawalsPending, processWithdrawalResponse } from "../../actions/withdrawal";
import { Status } from "../../enums/status";
import { Chip } from "@mui/material";
import { useNavigate } from "react-router";
import toast, { Toaster } from "react-hot-toast";
import ConfirmReject from "../dialogs/ConfirmReject";

const WithdrawalRequest = ({ token, id, isAuthenticated, setIsAuthenticated }) => {
	const [rows, setRows] = useState(null);
	const [disabledAccept, setDisabledAccept] = useState(false);
	const [disabledReject, setDisabledReject] = useState(false);
	const [confirmReject, setConfirmReject] = useState({
        isOpen: false,
        title: "",
        question: "",
    });
	const navigate = useNavigate();

	const handleAcceptClick = (e, id) => {
		e.preventDefault();
		setConfirmReject({
			isOpen:true,
			title: "Approve Withdrawal",
			question: "Are you sure to accept this withdrawal?",
			onConfirm: () =>{
				setConfirmReject({
					...confirmReject,
					isOpen: false,
				});
				setDisabledAccept(true);
				processWithdrawalResponse(token, id, true, (res) => {
					if (!res.error) {
						setDisabledAccept(false);
						getWithdrawalsPending(token, id, (res) => {
							if (!res.error) {
								setRows(res.data);
							}
						});
					} else {
						toast.error(res.message);
						setDisabledAccept(false);
					}
				});
			},
			onReject: () => {
				setConfirmReject({
					...confirmReject,
					isOpen: false,
				});
			}
		});		
	};

	const handleCancelClick = (e, id) => {
		e.preventDefault();
		setConfirmReject({
			isOpen:true,
			title: "Reject Withdrawal",
			question: "Are you sure to reject this withdrawal?",
			onConfirm: () =>{
				setConfirmReject({
					...confirmReject,
					isOpen: false,
				});
				setDisabledReject(true);
				processWithdrawalResponse(token, id, false, (res) => {
					if (!res.error) {
						setDisabledAccept(false);
						getWithdrawalsPending(token, id, (res) => {
							if (!res.error) {
								setRows(res.data);
							}
						});
					} else {
						setDisabledAccept(false);
					}
				});
			},
			onReject: () => {
				setConfirmReject({
					...confirmReject,
					isOpen: false,
				});
			}
		});		
	};

	function renderStatus(status) {
		const colors = {
			[Status.PROCESSED]: "success",
			[Status.PENDING]: "default",
			[Status.EXPIRED]: "warning",
			[Status.DECLINED]: "warning",
			[Status.WAITING]: "default",
			[Status.APPROVED]: "success",
		};

		return <Chip label={status} color={colors[status]} size="small" />;
	}

	const columns = [
		{ field: "createdAt", headerName: "Date", flex: 1.5, minWidth: 175 },
		{ field: "username", headerName: "Username", flex: 1.5, minWidth: 100 },
		{ field: "agent", headerName: "Agent", flex: 1.5, minWidth: 100 },
		{ field: "network", headerName: "Network", flex: 1.5, minWidth: 75 },
		{ field: "cryptoCurrency", headerName: "Crypto", flex: 1.5, minWidth: 75 },
		{
			field: "status",
			headerName: "Status",
			flex: 0.5,
			minWidth: 100,
			renderCell: (params) => renderStatus(params.value),
		},
		{ field: "cryptoAmount", headerName: "Amount", flex: 1.5, minWidth: 75 },
		{ field: "exchangeRate", headerName: "Exchange", flex: 1.5, minWidth: 75 },
		{ field: "currencyAmount", headerName: "USD", flex: 1.5, minWidth: 75 },
		{ field: "fees", headerName: "Fees", flex: 1.5, minWidth: 75 },
		{ field: "total", headerName: "Total", flex: 1.5, minWidth: 75 },
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			width: 100,
			cellClassName: "actions",
			getActions: ({ id }) => {
				return [
					<GridActionsCellItem
						icon={<CheckCircleIcon style={{ color: "green" }} />}
						label="Accept"
						onClick={(e) => handleAcceptClick(e, id)}
						disabled={disabledAccept}
					/>,
					<GridActionsCellItem
						icon={<CancelIcon style={{ color: "red" }} />}
						label="Reject"
						onClick={(e) => handleCancelClick(e, id)}
						disabled={disabledReject}
					/>,
				];
			},
		},
	];

	useEffect(() => {
		getWithdrawalsPending(token, id, (res) => {
			if (!res.error) {
				setRows(res.data);
			} else {
				if (res.message.toString().includes("Token Expired")) {
					navigate("/logout");
				}
			}
		});
	}, []);

	return (
		<>
			{rows ? (
				<>
					<DataGrid
						checkboxSelection={false}
						rows={rows}
						columns={columns}
						getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd")}
						initialState={{
							pagination: { paginationModel: { pageSize: 20 } },
						}}
						pageSizeOptions={[10, 20, 50]}
						disableColumnResize
						density="compact"
						slotProps={{
							filterPanel: {
								filterFormProps: {
									logicOperatorInputProps: {
										variant: "outlined",
										size: "small",
									},
									columnInputProps: {
										variant: "outlined",
										size: "small",
										sx: { mt: "auto" },
									},
									operatorInputProps: {
										variant: "outlined",
										size: "small",
										sx: { mt: "auto" },
									},
									valueInputProps: {
										InputComponentProps: {
											variant: "outlined",
											size: "small",
										},
									},
								},
							},
						}}
					/>
					<Toaster />
					<ConfirmReject confirmDialog={confirmReject}/>
				</>
			) : (
				<Grid container sx={{ justifyContent: "center", alignContent: "center", textAlign: "center" }}>
					<Box>
						<CircularProgress color="primary" size={80} thickness={5} />
					</Box>
				</Grid>
			)}
		</>
	);
};

export default WithdrawalRequest;
